import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useStudentAttendanceHook } from '../hooks/useStudentAttendanceHook';
import { useStudentAttendanceByUserHook } from '../hooks/useStudentAttendanceByUserHook';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';
import {
  TAttendanceByDateRecord,
  TAttendanceByDateResponse,
  TAttendanceByUserResponse,
} from '../../../../apis/__shared/attendance.interfaces';
import { TUserDetailsSimplifiedObject } from '../../../../apis/users/users.interfaces';
import { simplifiedStudentUserListToMap } from '../../../../utils/helpers';
import { useStudentsState } from './students.context';
import { useStudentFiltersState } from './studentFilters.context';
import dayjs, { Dayjs } from 'dayjs';

/** Stable fallback so consumers do not see a new `{}` reference every render. */
const EMPTY_ATTENDANCE_BY_USER: TAttendanceByUserResponse = {};

export enum ATTENDANCE_VIEW_MODE {
  BY_DATE = 'byDate',
  BY_USER = 'byUser',
}

type TStudentAttendanceAppState = {
  isAttendanceTrackerOpen: boolean;
  isEditMode: boolean;
  isCreateMode: boolean;
  selectedAttendance: TAttendanceByDateRecord | null;
  errorMessage?: string;
  isLoading: boolean;
  userParams: TPaginationParams;
  attendanceTrackerPayload: TAttendanceByDateResponse;
  dateRange: {
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
  };
  viewMode: ATTENDANCE_VIEW_MODE;
  selectedStudentId?: string;
  grade?: string;
  section?: string;
};

enum ACTION_TYPE {
  SET_IS_ATTENDANCE_TRACKER_OPEN = 'SET_IS_ATTENDANCE_TRACKER_OPEN',
  SET_IS_EDIT_MODE = 'SET_IS_EDIT_MODE',
  SET_IS_CREATE_MODE = 'SET_IS_CREATE_MODE',
  SET_SELECTED_ATTENDANCE = 'SET_SELECTED_ATTENDANCE',
  SET_REQUEST_PARAMS = 'SET_REQUEST_PARAMS',
  UPDATE_STUDENT_ATTENDANCE = 'UPDATE_STUDENT_ATTENDANCE',
  UPDATE_ATTENDANCE_TRACKER_PAYLOAD = 'UPDATE_ATTENDANCE_TRACKER_PAYLOAD',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_DATE_RANGE = 'SET_DATE_RANGE',
  SET_VIEW_MODE = 'SET_VIEW_MODE',
  SET_SELECTED_STUDENT_ID = 'SET_SELECTED_STUDENT_ID',
  SET_GRADE_SECTION = 'SET_GRADE_SECTION',
}

type Action =
  | { type: ACTION_TYPE.SET_IS_ATTENDANCE_TRACKER_OPEN; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_EDIT_MODE; payload: boolean }
  | { type: ACTION_TYPE.SET_IS_CREATE_MODE; payload: boolean }
  | {
      type: ACTION_TYPE.SET_SELECTED_ATTENDANCE;
      payload: TAttendanceByDateRecord | null;
    }
  | { type: ACTION_TYPE.SET_REQUEST_PARAMS; payload: TPaginationParams }
  | {
      type: ACTION_TYPE.UPDATE_STUDENT_ATTENDANCE;
      payload: TAttendanceByDateRecord[];
    }
  | {
      type: ACTION_TYPE.UPDATE_ATTENDANCE_TRACKER_PAYLOAD;
      payload: TAttendanceByDateResponse;
    }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | {
      type: ACTION_TYPE.SET_DATE_RANGE;
      payload: { fromDate: Dayjs | null; toDate: Dayjs | null };
    }
  | { type: ACTION_TYPE.SET_VIEW_MODE; payload: ATTENDANCE_VIEW_MODE }
  | { type: ACTION_TYPE.SET_SELECTED_STUDENT_ID; payload?: string }
  | {
      type: ACTION_TYPE.SET_GRADE_SECTION;
      payload: { grade?: string; section?: string };
    };

const getDefaultDateRange = () => {
  const now = dayjs();
  const startOfMonth = now.startOf('month');
  return {
    fromDate: startOfMonth,
    toDate: now,
  };
};

const initialStudentAttendanceState: TStudentAttendanceAppState = {
  isAttendanceTrackerOpen: false,
  isEditMode: false,
  isCreateMode: false,
  selectedAttendance: null,
  attendanceTrackerPayload: [],
  errorMessage: undefined,
  isLoading: false,
  userParams: {
    page: 1,
    limit: 10,
  },
  dateRange: getDefaultDateRange(),
  viewMode: ATTENDANCE_VIEW_MODE.BY_DATE,
  selectedStudentId: undefined,
  grade: undefined,
  section: undefined,
};

type TState = {
  studentAttendanceAppState: TStudentAttendanceAppState;
  studentAttendanceRecords: TAttendanceByDateResponse;
  studentAttendanceByUserRecords: TAttendanceByUserResponse;
  simplifiedStudentObject: TUserDetailsSimplifiedObject;
};

const StudentAttendanceContext = createContext<TState | undefined>(undefined);
const StudentAttendanceDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const StudentAttendanceReducer = (
  appState: TStudentAttendanceAppState,
  action: Action
): TStudentAttendanceAppState => {
  switch (action.type) {
    case ACTION_TYPE.SET_IS_ATTENDANCE_TRACKER_OPEN:
      return { ...appState, isAttendanceTrackerOpen: action.payload };
    case ACTION_TYPE.SET_IS_EDIT_MODE:
      return { ...appState, isEditMode: action.payload };
    case ACTION_TYPE.SET_IS_CREATE_MODE:
      return { ...appState, isCreateMode: action.payload };
    case ACTION_TYPE.SET_SELECTED_ATTENDANCE:
      return { ...appState, selectedAttendance: action.payload };
    case ACTION_TYPE.SET_REQUEST_PARAMS:
      return {
        ...appState,
        userParams: { ...appState.userParams, ...action.payload },
      };
    case ACTION_TYPE.UPDATE_STUDENT_ATTENDANCE:
      return { ...appState, ...action.payload };
    case ACTION_TYPE.UPDATE_ATTENDANCE_TRACKER_PAYLOAD:
      return { ...appState, attendanceTrackerPayload: action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_DATE_RANGE:
      return { ...appState, dateRange: action.payload };
    case ACTION_TYPE.SET_VIEW_MODE:
      return { ...appState, viewMode: action.payload };
    case ACTION_TYPE.SET_SELECTED_STUDENT_ID:
      return { ...appState, selectedStudentId: action.payload };
    case ACTION_TYPE.SET_GRADE_SECTION:
      return {
        ...appState,
        grade: action.payload.grade,
        section: action.payload.section,
      };
    default:
      throw new Error('Unhandled action type');
  }
};

export const StudentAttendanceProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [StudentAttendanceAppState, dispatch] = useReducer(
    StudentAttendanceReducer,
    initialStudentAttendanceState
  );

  const { filteredStudents } = useStudentsState();
  const filters = useStudentFiltersState();

  const fromDate = useMemo(() => {
    return StudentAttendanceAppState.dateRange.fromDate
      ? StudentAttendanceAppState.dateRange.fromDate.startOf('day').toDate()
      : undefined;
  }, [StudentAttendanceAppState.dateRange.fromDate]);

  const toDate = useMemo(() => {
    return StudentAttendanceAppState.dateRange.toDate
      ? StudentAttendanceAppState.dateRange.toDate.endOf('day').toDate()
      : undefined;
  }, [StudentAttendanceAppState.dateRange.toDate]);

  const {
    data: StudentAttendanceResponse,
    isLoading: StudentAttendanceResponseLoading,
    isError: StudentAttendanceResponseLoadingError,
    isSuccess: StudentAttendanceResponseLoadingDataSuccess,
    error: StudentAttendanceResponseDataError,
  } = useStudentAttendanceHook(
    StudentAttendanceAppState.userParams,
    filters.grade !== 'select' ? filters.grade : undefined,
    filters.section !== 'select' ? filters.section : undefined,
    fromDate,
    toDate
  );

  const {
    data: StudentAttendanceByUserResponse,
    isLoading: StudentAttendanceByUserResponseLoading,
    isError: StudentAttendanceByUserResponseLoadingError,
    isSuccess: StudentAttendanceByUserResponseLoadingDataSuccess,
    error: StudentAttendanceByUserResponseDataError,
  } = useStudentAttendanceByUserHook(
    StudentAttendanceAppState.userParams,
    filters.grade !== 'select' ? filters.grade : undefined,
    filters.section !== 'select' ? filters.section : undefined,
    fromDate,
    toDate,
    StudentAttendanceAppState.selectedStudentId,
    {
      enabled:
        StudentAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_USER,
    }
  );

  const loadingPageData = useMemo(
    () =>
      StudentAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_DATE
        ? StudentAttendanceResponseLoading
        : StudentAttendanceByUserResponseLoading,
    [
      StudentAttendanceAppState.viewMode,
      StudentAttendanceResponseLoading,
      StudentAttendanceByUserResponseLoading,
    ]
  );

  const loadingPageDataSuccess = useMemo(
    () =>
      StudentAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_DATE
        ? StudentAttendanceResponseLoadingDataSuccess
        : StudentAttendanceByUserResponseLoadingDataSuccess,
    [
      StudentAttendanceAppState.viewMode,
      StudentAttendanceResponseLoadingDataSuccess,
      StudentAttendanceByUserResponseLoadingDataSuccess,
    ]
  );

  const loadingPageDataError = useMemo(
    () =>
      StudentAttendanceAppState.viewMode === ATTENDANCE_VIEW_MODE.BY_DATE
        ? StudentAttendanceResponseLoadingError
        : StudentAttendanceByUserResponseLoadingError,
    [
      StudentAttendanceAppState.viewMode,
      StudentAttendanceResponseLoadingError,
      StudentAttendanceByUserResponseLoadingError,
    ]
  );

  useEffect(() => {
    onStateChangeHandler();
    // eslint-disable-next-line
  }, [
    StudentAttendanceResponseDataError,
    StudentAttendanceByUserResponseDataError,
    loadingPageDataError,
    StudentAttendanceResponseLoading,
    StudentAttendanceByUserResponseLoading,
    loadingPageData,
    loadingPageDataSuccess,
    StudentAttendanceResponseLoadingError,
    StudentAttendanceByUserResponseLoadingError,
    StudentAttendanceAppState.viewMode,
  ]);

  const onStateChangeHandler = () => {
    if (loadingPageData) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (loadingPageDataSuccess) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (loadingPageDataError) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: StudentAttendanceResponseLoadingError
          ? (StudentAttendanceResponseDataError?.response?.data.message ??
            ERROR_MESSAGE.GET_STUDENT_ATTENDANCE_FAILURE)
          : '',
      });
    }
  };

  const currentState = useMemo<TState>(
    () => ({
      studentAttendanceAppState: StudentAttendanceAppState,
      studentAttendanceRecords: StudentAttendanceResponse ?? [],
      studentAttendanceByUserRecords:
        StudentAttendanceByUserResponse ?? EMPTY_ATTENDANCE_BY_USER,
      simplifiedStudentObject: simplifiedStudentUserListToMap(filteredStudents),
      attendanceTrackerPayload:
        StudentAttendanceAppState.attendanceTrackerPayload,
    }),
    [
      StudentAttendanceAppState,
      StudentAttendanceResponse,
      StudentAttendanceByUserResponse,
      filteredStudents,
    ]
  );

  return (
    <StudentAttendanceDispatchContext.Provider value={dispatch}>
      <StudentAttendanceContext.Provider value={currentState}>
        {children}
      </StudentAttendanceContext.Provider>
    </StudentAttendanceDispatchContext.Provider>
  );
};

export const useStudentAttendanceState = () => {
  const state = useContext(StudentAttendanceContext);
  if (state === undefined) {
    throw new Error(
      'useStudentAttendanceState must be used within a StudentAttendanceProvider'
    );
  }
  return state;
};

export const useStudentAttendanceActions = () => {
  const dispatch = useContext(StudentAttendanceDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useStudentAttendanceActions dispatch must be within a StudentAttendanceProvider'
    );
  }

  const updateSearchParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_REQUEST_PARAMS, payload: params });
  };

  const updateAttendanceTrackerPayload = (data: TAttendanceByDateResponse) => {
    dispatch({
      type: ACTION_TYPE.UPDATE_ATTENDANCE_TRACKER_PAYLOAD,
      payload: data,
    });
  };

  const setIsAttendanceTrackerOpen = (isOpen: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_IS_ATTENDANCE_TRACKER_OPEN,
      payload: isOpen,
    });
  };

  const setIsEditMode = (isEditMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_EDIT_MODE, payload: isEditMode });
  };

  const setIsCreateMode = (isCreateMode: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_IS_CREATE_MODE, payload: isCreateMode });
  };

  const setSelectedAttendance = (
    attendance: TAttendanceByDateRecord | null
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_ATTENDANCE,
      payload: attendance,
    });
  };

  const setDateRange = (fromDate: Dayjs | null, toDate: Dayjs | null) => {
    dispatch({
      type: ACTION_TYPE.SET_DATE_RANGE,
      payload: { fromDate, toDate },
    });
  };

  const setViewMode = (viewMode: ATTENDANCE_VIEW_MODE) => {
    dispatch({
      type: ACTION_TYPE.SET_VIEW_MODE,
      payload: viewMode,
    });
  };

  const setSelectedStudentId = (studentId?: string) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_STUDENT_ID,
      payload: studentId,
    });
  };

  const setGradeSection = (grade?: string, section?: string) => {
    dispatch({
      type: ACTION_TYPE.SET_GRADE_SECTION,
      payload: { grade, section },
    });
  };

  return {
    updateSearchParams,
    updateAttendanceTrackerPayload,
    setIsAttendanceTrackerOpen,
    setIsEditMode,
    setIsCreateMode,
    setSelectedAttendance,
    setDateRange,
    setViewMode,
    setSelectedStudentId,
    setGradeSection,
  };
};
